import { DotPadLine } from "./DotPadLine";
import { DotPadSendMakeModel, DotPadSendType } from "./DotPadSendMakeModel";
import dotPadTrace from "./dotPadTrace";

const RefreshType = {
    NONE: 'NONE',
    TYPE_A: 'TYPE_A',
    TYPE_B: 'TYPE_B',
    TYPE_C: 'TYPE_C',
};

const RefreshMode = {
    NONE: 'NONE',
    ALL_DISPLAY: 'ALL_DISPLAY',
    GRAPHIC_DISPLAY: 'GRAPHIC_DISPLAY',
    TEXT_DISPLAY: 'TEXT_DISPLAY',
    LIVE_DISPLAY: 'LIVE_DISPLAY',
};

// D2 셀 기기의 라인 ACK 대기 한도(ms).
// 기기 응답은 90~364ms 로 빠르지만, 드로잉 중 메인 스레드가 막히면 응답 처리가 그만큼 밀린다.
// 그 지연을 흡수하기 위한 완충값이다.
const D2_ACK_WAIT_TIME = 1300;

// 프레임을 선점할 때, 직전에 나간 쓰기의 응답을 기다려 주는 한도(ms).
// D2 는 라인을 순서대로 처리하는데 처리 중에 도착한 쓰기는 조용히 버려진다. 응답을 기다리지
// 않고 다음 라인을 쓰면 그 라인이 묻히고, 1300ms 뒤 타임아웃이 나서야 재전송으로 복구된다.
// 실측에서 응답은 선점 후 30~70ms 에 왔다. 한도는 그보다 넉넉히 두되, 응답이 영영 오지 않는
// 경우에도 멈추지 않도록 상한을 둔다.
const STALE_ACK_GRACE = 600;

class DotPadSendModule {
    constructor(dotPad) {
        this.dotPadLineList = [];
        this.dotCommandSendReady = true;
        this.sendTime = new Date();
        this.sendCnt = 0;
        this.dotPadLine = null;
        this.restart = false;
        this.currentIndex = 0;
        this.isFunctionRunning = false;
        this.dotPad = dotPad;
        this.baseDisplayComplete = true; // 오토리프레시를 제외한 최초출력이 완료되었는지 체크
        this.invalidatedLines = new Set();

        this.refreshType = RefreshType.TYPE_C;
        this.refreshCount = 0;
        this.refreshItem = null;
        // 예약된 재출력 setTimeout 의 핸들. refreshItem(콜백 함수)만 들고 있으면 clearTimeout 이 되지 않는다.
        this.refreshTimerId = null;
        this.refreshLine = [];
        this.refreshMode = RefreshMode.NONE;
        // 재출력이 세운 라인 번호. 새 사용자 프레임이 재출력을 취소할 때 "재출력이 세운 것"만
        // 골라내기 위한 소유 표시다. 사용자 라인까지 취소하면 그 내용이 영영 전송되지 않는다.
        this.refreshOwned = new Set();

        // 전송 루프의 세대 번호. 루프는 setTimeout 체인이라 밖에서 멈출 수 없으므로,
        // 버려야 할 때 이 번호를 올려 예전 루프가 스스로 빠져나가게 한다.
        this.frameEpoch = 0;

        // 선점 직전에 나간 쓰기의 응답을 언제까지 기다릴지. 0 이면 기다리는 것이 없다.
        this.staleAckDeadline = 0;

        // 기기로 나간 쓰기가 아직 응답을 못 받았는지. dotCommandSendReady 로는 이걸 알 수 없다.
        // 그 값은 await writeCmdTest(...) 가 풀린 뒤에야 false 가 되는데, BLE 쓰기는 시간이
        // 걸려서 그 사이에 선점이 들어오면 "나간 쓰기가 없다"로 잘못 읽힌다.
        // (실측: line.tx 7ms 뒤에 선점, 그때 dotCommandSendReady 는 아직 true 였다)
        this.writeOutstanding = false;

        this.sendType = DotPadSendType.SINGLE_LINE
    }

    /**
     - 예약된 재출력 타이머를 실제로 취소한다.
     - 재출력은 사용자 데이터 전송이 끝난 뒤 도는 사이클이라, 새 사용자 프레임이 들어오면 폐기해야 한다.
     - 남겨두면 지연 발화해서 진행 중인 사용자 프레임의 currentIndex 를 되돌리고 refreshCount 를 올린다.
     */
    clearRefreshTimer() {
        if (this.refreshTimerId !== null) {
            clearTimeout(this.refreshTimerId);
            dotPadTrace.log("refresh.cancel", { refreshCount: this.refreshCount, mode: this.refreshMode });
            this.refreshTimerId = null;
        }
        this.refreshItem = null;
    }

    /**
     - 재출력용으로 라인을 세운다.
     - 소유 표시를 남겨, 새 사용자 프레임이 재출력을 취소할 때 사용자 라인과 구분되게 한다.
     */
    refreshOwnedLine(lineId, mode) {
        const line = this.dotPadLineList[lineId];
        if (!line) return;

        line.refresh(mode);
        this.refreshOwned.add(lineId);
    }

    /**
     - 재출력이 세운 라인만 취소한다. 아직 못 나간 사용자 라인은 그대로 둔다.
     */
    cancelOwnedRefreshLines() {
        if (this.refreshOwned.size < 1) return;

        for (const lineId of this.refreshOwned) {
            const line = this.dotPadLineList[lineId];
            if (!line) continue;

            line.setRequestReady(false);
            line.setReceiveAck(true);
        }
        this.refreshOwned.clear();
    }

    /**
     - 재출력 콜백을 예약한다. 발화 시점에 사용자 프레임이 진행 중이면 재출력을 건너뛴다.
     */
    scheduleRefresh(task, delay, label) {
        this.clearRefreshTimer();

        const scheduledCount = this.refreshCount;

        this.refreshItem = () => {
            this.refreshTimerId = null;
            this.refreshItem = null;

            // 사용자 프레임이 진행 중인데 재출력 맥락이 사라졌으면(refreshCount 가 0으로 리셋됨)
            // 이 재출력은 이미 지난 화면 기준이다. 여기서 currentIndex 를 되돌리고 refreshCount 를
            // 올리면 진행 중인 사용자 프레임이 뒤로 밀리고, 뒤이어 들어오는 전송이 아직 못 나간
            // 사용자 라인까지 취소해버린다. 이 프레임이 끝나면 재출력 사이클은 다시 시작된다.
            if (this.isFunctionRunning && this.refreshCount === 0) {
                dotPadTrace.loud("refresh.skip", { label, scheduledCount, reason: "user-frame-running" });
                return;
            }

            dotPadTrace.log("refresh.fire", { label, refreshCount: this.refreshCount, running: this.isFunctionRunning });
            task();
        };

        dotPadTrace.log("refresh.schedule", { label, refreshCount: scheduledCount, delay });
        this.refreshTimerId = setTimeout(this.refreshItem, delay);
    }

    // 라인목록 초기화
    clearDotPadLine(){
        this.dotPadLineList = []
    }

    // 닷패드 라인 추가
    addDotPadLine(rowCellSize, colCellSize) {
        // 기존 설정과 동일한지 체크
        if (this.dotPadLineList.length > 0) {
            const existingLastLine = this.dotPadLineList[this.dotPadLineList.length - 1];
            
            // 마지막 라인 번호 셀 크기가 동일하면 재설정하지 않음
            if ( 
                existingLastLine && 
                existingLastLine.lineId === rowCellSize &&
                existingLastLine.numberCellColumns === colCellSize) {
                return;
            }
        }

        this.dotPadLineList = [];

        // 20셀 세팅 (600:requestTime)
        this.dotPadLineList.push(new DotPadLine(0, 600, 20));
        
        // 디바이스 크기에 맞게 세팅 (600:requestTime)
        for (let i = 1; i <= rowCellSize; i++) {
            this.dotPadLineList.push(new DotPadLine(i, 600, colCellSize));
        }
    }

    // 해당 라인에 커맨드 세팅
    setDotPadLineCommand(line, seqNum, startCellIndex, sendData, init = true) {
        if (init) {
            if (this.refreshCount > 0) {
                // 재출력 사이클을 취소하고 새 사용자 데이터를 우선한다.
                //
                // 취소 대상은 "재출력이 세운 라인"으로 한정한다. 라인의 lineData 는 전송 전에 이미
                // 최신 상태로 갱신되기 때문에, 아직 못 나간 사용자 라인까지 취소하면 기기 화면과
                // lineData 가 어긋난 채로 남고 이후 diff 가 그 부분을 "변화 없음"으로 판정해
                // 영영 전송하지 않는다. (재출력이 lineData 전체를 다시 뿌릴 때에야 뒤늦게 나타난다)
                // 실측 로그에서 사용자 라인 9개[2..10]가 한 번에 취소되던 지점이 여기다.
                if (this.refreshOwned.size > 0) {
                    dotPadTrace.log("refresh.cancelLines", () => ({
                        line,
                        canceled: [...this.refreshOwned],
                        refreshCount: this.refreshCount,
                        running: this.isFunctionRunning
                    }));
                }
                this.cancelOwnedRefreshLines();

                // 재출력 사이클은 사용자 프레임이 끝난 뒤에도 "같은 루프 안에서" 이어진다.
                // (graphicRefresh 가 currentIndex 를 1로 되돌릴 뿐 cmd.end 가 나지 않는다)
                // 그래서 isFunctionRunning 은 계속 true 다. 여기서 루프를 끝내주지 않으면 뒤따르는
                // sendCommand() 가 새 프레임을 시작하지 못하고 restart 만 세우고, restart 는
                // currentIndex 를 0으로 되돌려 라인을 처음부터 전부 다시 보낸다. cmd.end 가 없으니
                // 다음 프레임도 같은 길을 가서 영영 끝나지 않는다.
                // 실측(320A, 54초): 재시작 14회 중 13회가 이 경로. 재시작이 낀 프레임 평균 10,196ms
                // 대 없는 프레임 1,124ms. 최악 프레임은 15,250ms / 재시작 12회 / 10라인 화면에
                // 라인 전송 83회.
                this.abandonRunningFrame("refresh-preempted");
            }

            // 새 사용자 프레임이 시작되므로 예약된 재출력은 폐기한다.
            this.clearRefreshTimer();

            this.refreshCount = 0;
            this.currentIndex = 0;
        } else {
            this.currentIndex = 0;
        }

        // 이 라인은 사용자 데이터로 덮어쓰므로 더 이상 재출력 소유가 아니다.
        this.refreshOwned.delete(line);
        
        if (this.refreshMode === RefreshMode.LIVE_DISPLAY && !this.refreshLine.includes(line)){
            this.refreshLine.push(line);
            this.dotPadLineList[line].initRefreshLiveData(startCellIndex, sendData);
        } else {
            this.dotPadLineList[line].setRefreshLiveData(startCellIndex, sendData);
        }
        this.dotPadLineList[line].setCommand(seqNum, startCellIndex, sendData);

        // 여기서 lineData 가 갱신된다. 즉 이 시점부터 "기기가 받았다"고 기록된 셈이라,
        // 이 라인이 끝내 전송되지 않으면 이후 diff 가 영영 변화 없음으로 판정한다.
        // line.tx 와 짝을 맞춰 보면 큐에만 들어가고 안 나간 라인을 바로 셀 수 있다.
        dotPadTrace.log("line.queue", { line, init, refreshCount: this.refreshCount });
    }

    // 데이터 비교후 다른 부분만 출력
    setDotPadLineCommandDiff(line, seqNum, sendData, init = true) {
        if (this.invalidatedLines.has(line)) {
            this.setDotPadLineCommand(line, seqNum, 0, sendData, init);
            this.invalidatedLines.delete(line);
            return true;
        }
        const result = this.compareString(sendData, this.dotPadLineList[line].getLineData());
        if (result !== null) {
            const [start, end] = result;
            const diffData = sendData.slice(start, end + 1);
            this.setDotPadLineCommand(line, seqNum, parseInt(start / 2), diffData, init);
            return true
        }

        return false
    }

    /**
     - 돌고 있는 전송 루프를 버리고, 다음 sendCommand() 가 새 프레임으로 시작할 수 있게 한다.
     -
     - refreshMode 는 건드리지 않는다. 호출 직전에 setLineDiff() 가 이번 프레임의 모드를
     - 이미 세워 두기 때문이다.
     */
    abandonRunningFrame(reason) {
        if (!this.isFunctionRunning) return false;

        dotPadTrace.loud("cmd.abandon", {
            reason,
            currentIndex: this.currentIndex,
            refreshCount: this.refreshCount,
            mode: this.refreshMode,
            // 대기가 걸렸는지 / 안 걸렸다면 왜인지를 덤프만 보고 가릴 수 있게 남긴다.
            outstanding: this.writeOutstanding,
            ready: this.dotCommandSendReady
        });

        // 버리는 시점에 기기로 나간 쓰기가 아직 응답을 못 받았다면, 그 응답이 올 때까지
        // 다음 쓰기를 미룬다. 기기가 이전 라인을 처리하는 중에 도착한 쓰기는 버려지기 때문이다.
        // (실측: 선점 직후 보낸 라인이 1264ms 동안 무응답 → 재전송하니 44ms 만에 응답.
        //  다음 라인들은 60~120ms 로 정상. 즉 느린 게 아니라 첫 쓰기가 묻힌 것이다)
        const hadPendingWrite = this.writeOutstanding;
        this.staleAckDeadline = hadPendingWrite ? Date.now() + STALE_ACK_GRACE : 0;
        if (hadPendingWrite) {
            dotPadTrace.log("stale.hold", { line: this.currentIndex });
        }

        this.frameEpoch += 1;
        this.isFunctionRunning = false;
        this.restart = false;
        // 응답을 못 받은 채로 두면 새 프레임의 첫 라인이 ACK 타임아웃 경로로 빠진다.
        // 실제 대기는 위의 staleAckDeadline 이 맡으므로 여기서는 되돌려 둔다.
        // (restart 분기도 같은 이유로 true 로 되돌린다)
        this.dotCommandSendReady = true;
        return true;
    }

    cancelPendingOutput() {
        this.clearRefreshTimer();
        this.abandonRunningFrame('screen-leave');
        for (const line of this.dotPadLineList) {
            line.setRequestReady(false);
            line.setReceiveAck(true);
            this.invalidatedLines.add(line.getLineId());
        }
        this.refreshOwned.clear();
        this.refreshCount = 0;
        this.baseDisplayComplete = true;
    }

    /**
     - 선점 직전에 나간 쓰기의 응답을 아직 기다리는 중인가.
     - 한도를 넘기면 포기한다. 응답이 영영 오지 않는 경우에도 전송이 멈추면 안 된다.
     */
    isWaitingStaleAck() {
        if (!this.staleAckDeadline) return false;

        if (Date.now() >= this.staleAckDeadline) {
            dotPadTrace.loud("stale.giveUp", { grace: STALE_ACK_GRACE });
            this.staleAckDeadline = 0;
            return false;
        }
        return true;
    }

    // 발송준비 세팅
    setDotCommandSendReady(ready) {
        this.dotCommandSendReady = ready
    }

    async refresh() {
        for (let i = 0; i < this.dotPadLineList.length; i++) {
            this.refreshOwnedLine(i);
        }

        await this.sendCommand();
    }
    
    checkBaseDisplay() {
        return (this.isFunctionRunning && this.currentIndex >= this.dotPadLineList.length - 1) || this.refreshCount > 0 || !this.isFunctionRunning
    }
    
    getIsFunctionRunning() {
        return this.isFunctionRunning
    }

    setDisplayComplete() {
        if (this.checkBaseDisplay()) {
            this.baseDisplayComplete = true
        }
    }

    // 최초출력 완료 체크
    getDisplayComplete() {
        this.setDisplayComplete()
        return this.baseDisplayComplete
    }

    setSendType(sendType) {
        this.sendType = sendType
    }

    getSendType() {
        return this.sendType
    }

    async sendCommand(refresh = false) {
        if (!this.isFunctionRunning) {
            this.isFunctionRunning = true
            this.currentIndex = 0
            this.frameEpoch += 1
            const epoch = this.frameEpoch

            if (!refresh)
                this.refreshCount = 0

            const cellType = this.dotPad.deviceInfo.cellType
            const frameStartedAt = dotPadTrace.now()
            dotPadTrace.log("cmd.start", () => ({
                refresh,
                refreshCount: this.refreshCount,
                mode: this.refreshMode,
                lines: this.dotPadLineList.filter((l) => l.getRequestReady()).map((l) => l.getLineId())
            }))

            const processNextLine = async () => {
                // 이 루프가 버려졌으면(새 프레임이 선점) 여기서 끝낸다.
                if (this.frameEpoch !== epoch) {
                    dotPadTrace.log("cmd.abandoned", { epoch, now: this.frameEpoch })
                    return
                }

                // 선점 직전에 나간 쓰기의 응답을 기다리는 동안에는 아무것도 보내지 않는다.
                // 여기서 보내면 기기가 그 쓰기를 버리고, 1300ms 뒤 타임아웃이 나서야 복구된다.
                if (this.isWaitingStaleAck()) {
                    setTimeout(processNextLine, 1)
                    return
                }

                if (this.currentIndex < this.dotPadLineList.length) {
                    // 재시작일경우 값 초기화
                    if (this.restart) {
                        dotPadTrace.log("cmd.restartApply", { from: this.currentIndex })
                        this.currentIndex = 0
                        this.restart = false
                        this.dotCommandSendReady = true
                    }
                    else {
                        // 전체출력일때 시작라인 세팅
                        if (this.currentIndex > 0 && (this.sendType === DotPadSendType.ALL_LINE || this.sendType === DotPadSendType.ALL_PARTIAL)) {
                            let list = this.dotPadLineList.filter(line => line.getRequestReady());
                            this.currentIndex = list.length > 0 ? list[0].getLineId() : this.currentIndex;
                        } 

                        this.dotPadLine = this.dotPadLineList[this.currentIndex]

                        // 발송해야하는지 확인
                        if (this.dotPadLine.getRequestReady() && this.dotCommandSendReady) {
                            // 발송 처리
                            if (this.currentIndex === 0 || this.sendType === DotPadSendType.SINGLE_LINE || this.sendType === DotPadSendType.SINGLE_PARTIAL) {
                                this.dotPadLine.setSendStatus()
                            } else {
                                this.dotPadLine.setReceiveAck(false)
                            }

                            dotPadTrace.log("line.tx", { line: this.currentIndex, refreshCount: this.refreshCount })

                            // 쓰기가 "시작"되는 시점에 표시한다. await 가 풀린 뒤에 표시하면
                            // 그 사이에 들어온 선점이 나간 쓰기를 못 본다.
                            this.writeOutstanding = true

                            if (this.currentIndex >= 1 && this.refreshCount === 0) {
                                let sendData = DotPadSendMakeModel.getSendData(this.sendType, this.dotPadLineList, this.dotPadLine)

                                if (this.sendType === DotPadSendType.ALL_LINE || this.sendType === DotPadSendType.ALL_PARTIAL) {
                                    for (let i = this.currentIndex; i < this.dotPadLineList.length; i++) {
                                        this.dotPadLineList[i].setRequestReady(false);
                                    }
                                }

                                await this.dotPad.writeCmdTest(sendData);
                            } else {
                                let sendData = DotPadSendMakeModel.makeSingleLineData(this.dotPadLine)
                                await this.dotPad.writeCmdTest(sendData);
                            }

                            this.dotCommandSendReady = false;
                            
                            // 최초출력 완료 체크용
                            if (this.refreshCount === 0){
                                this.baseDisplayComplete = false;
                            }
                            
                            // 시간체크 시작
                            this.sendTime = new Date()
                            this.sendCnt = 0
                        }
                        // ACK 수신 대기중
                        else if (!this.dotPadLine.getReceiveAck()) {
                            // 시간체크
                            const nowTime = new Date() - this.sendTime;
                            let time = (() => {
                                const requestTime = this.dotPadLine?.getRequestTime?.() ?? 500;
                                if (
                                    this.currentIndex >= 1
                                    && this.refreshCount === 0
                                    && (
                                        this.sendType === DotPadSendType.ALL_LINE
                                        || this.sendType === DotPadSendType.ALL_PARTIAL
                                    )
                                ) {
                                    if (cellType === "D2")
                                        return (requestTime * this.dotPadLineList.length);
                                    else
                                        return 600;
                                } else if (cellType === "D2") {
                                    // 기기 응답 자체는 빠르다(320A 실측 ACK 왕복 90~364ms).
                                    // 600ms 를 넘기는 것은 마우스 드로잉 중 메인 스레드가 막혀
                                    // BLE 응답 콜백 처리가 밀리기 때문이다(같은 구간에 longtask 64ms + 50ms 관측).
                                    // 그 지연을 흡수하지 못하면 정상 응답 중인 기기에 중복 전송이 쌓여 더 느려지므로
                                    // 한도에 여유를 둔다. 근본 해결은 메인 스레드 부하를 줄이는 쪽이다.
                                    return Math.max(requestTime, D2_ACK_WAIT_TIME);
                                } else {
                                    return requestTime;
                                }
                            })();

                            if (nowTime > time) {
                                dotPadTrace.loud("line.ackTimeout", {
                                    line: this.currentIndex,
                                    waited: nowTime,
                                    limit: time,
                                    tryCount: this.sendCnt
                                })

                                // 재요청
                                if (this.sendCnt < 3) {
                                    // 재발송
                                    if (this.currentIndex >= 1 && this.refreshCount === 0) {
                                        if (this.sendType === DotPadSendType.ALL_LINE || this.sendType === DotPadSendType.ALL_PARTIAL) {
                                            // 이미 발송한걸로 취급되서 재발송시에 requestReady를 세팅
                                            for (let i = this.currentIndex; i < this.dotPadLineList.length; i++) {
                                                this.dotPadLineList[i].setRequestReady(true);
                                            }
                                        }

                                        let sendData = DotPadSendMakeModel.getSendData(this.sendType, this.dotPadLineList, this.dotPadLine)
                                        this.writeOutstanding = true
                                        await this.dotPad.writeCmdTest(sendData);

                                        if (this.sendType === DotPadSendType.ALL_LINE || this.sendType === DotPadSendType.ALL_PARTIAL) {
                                            // 발송 후 다시 false 처리
                                            for (let i = this.currentIndex; i < this.dotPadLineList.length; i++) {
                                                this.dotPadLineList[i].setRequestReady(false);
                                            }
                                        }
                                    } else {
                                        let sendData = DotPadSendMakeModel.makeSingleLineData(this.dotPadLine)
                                        this.writeOutstanding = true
                                        await this.dotPad.writeCmdTest(sendData);
                                    }
                                    this.sendTime = new Date();
                                    this.sendCnt += 1;
                                } else {
                                    // 수신받은 것으로 처리
                                    this.dotPadLine.setReceiveAck(true);
                                    this.dotCommandSendReady = true;
                                    this.baseDisplayComplete = true;
                                    // 응답을 포기했으므로 더 기다리지 않는다.
                                    this.writeOutstanding = false;
                                }
                            }
                        } else if (this.dotCommandSendReady) {
                            if (this.currentIndex >= 1 && this.refreshCount === 0) {
                                if (this.sendType === DotPadSendType.ALL_LINE || this.sendType === DotPadSendType.ALL_PARTIAL) {
                                    this.currentIndex = this.dotPadLineList.length
                                    this.dotPadLine = null
                                } else {
                                    this.currentIndex += 1;
                                }
                            } else {
                                this.currentIndex += 1;
                            }
                        } else {
                            this.dotCommandSendReady = true;
                        }
                    }

                    // DotPad320 D2셀의 경우 Refresh 처리
                    if (cellType === "D2")
                        this.refreshCommand()
                    
                    setTimeout(processNextLine, 1) // 0.001초 지연 후 다음 반복 실행
                }
                else {
                    dotPadTrace.log("cmd.end", {
                        ms: Math.round(dotPadTrace.now() - frameStartedAt),
                        refreshCount: this.refreshCount,
                        pendingRefresh: this.refreshTimerId !== null
                    })
                    this.isFunctionRunning = false
                    this.refreshMode = RefreshMode.NONE
                    this.baseDisplayComplete = true
                    this.writeOutstanding = false
                }
            }
            
            try {
                await processNextLine()
            }
            catch (e) {
                console.log("sendCommand Error: ", e)
            }
        }
        else {
            dotPadTrace.log("cmd.restartReq", {
                refresh,
                currentIndex: this.currentIndex,
                refreshCount: this.refreshCount
            })
            this.restart = true
        }
    }

    // Ack 세팅
    setDotPadLineReceiveAck(line, receiveAck) {
        // 기다리던 것은 "선점 직전 쓰기의 응답"이다. 어느 라인이든 응답이 온 것은 기기가
        // 그 작업을 끝냈다는 뜻이므로, 여기서 대기를 푼다.
        if (receiveAck) {
            this.writeOutstanding = false;

            if (this.staleAckDeadline) {
                dotPadTrace.log("stale.release", { line });
                this.staleAckDeadline = 0;
            }
        }

        if (line > 0 && (this.sendType === DotPadSendType.ALL_LINE || this.sendType === DotPadSendType.ALL_PARTIAL)) {
            for (let i = 1; i < this.dotPadLineList.length; i++) {
                this.dotPadLineList[i].setReceiveAck(receiveAck);
            }
        } else {
            const dotPadLine = this.dotPadLineList[line];
            if(dotPadLine){
                dotPadLine.setReceiveAck(receiveAck);
            }
        }
    }

    // 재출력 타입 세팅
    setRefreshType(type){
        this.refreshType = type
    }

    // 재출력
    refreshCommand() {
        if(this.dotPadLineList.length === this.currentIndex) {
            switch (this.refreshType) {
                case RefreshType.TYPE_A : this.refreshCommandFuncA(); break;
                case RefreshType.TYPE_B : this.refreshCommandFuncB(); break;
                case RefreshType.TYPE_C : this.refreshCommandFuncC(); break;
                default: break;
            }
        }
    }

    // 재출력 A타입
    refreshCommandFuncA() {
        // 전체출력후 재출력
        if (this.refreshCount < 3) {
            this.currentIndex = 0;

            for (let i = 0; i < this.dotPadLineList.length; i++) {
                this.refreshOwnedLine(i);
            }

            this.refreshCount += 1;
        }
    }

    // 재출력 B타입
    refreshCommandFuncB() {
        // 전체출력후 재출력 (refreshMode 가 ALL_DISPLAY 일 때의 경로)
        //
        // 2단계(라인 4~10, 7줄)를 graphicRefresh 와 달리 지연 없이 돌렸다. 게다가 currentIndex 를
        // 매번 되돌려 루프가 끝에 닿지 못하니 cmd.end 가 나지 않고, cmd.end 가 없으면 refreshMode
        // 도 NONE 으로 돌아가지 못한다. ALL_DISPLAY 는 setRefreshMode 에서 빠져나갈 길이 없는
        // 상태라(그 분기가 ALL_DISPLAY 를 제외한다) 한 번 들어오면 계속 여기를 돈다.
        //
        // 실측(320A): 그리는 내내 ALL_DISPLAY 가 21.5초 지속, 그 구간 라인 전송 110회 중 82회
        // (75%)가 재출력이었다. 사용자가 그린 것은 25%만 나간다.
        //
        // graphicRefresh 와 같은 모양으로 맞춘다. 2단계를 500ms 뒤로 미루면 그 사이 루프가 끝에
        // 닿아 cmd.end 가 나고, refreshMode 도 NONE 으로 풀린다.
        dotPadTrace.log("refresh.all", { refreshCount: this.refreshCount, mode: this.refreshMode });

        if (this.refreshCount < 3) {
            this.currentIndex = 0;

            for (let i = 0; i <= 3; i++) {
                this.refreshOwnedLine(i);
            }

            this.refreshCount += 1;
        } else if (this.refreshCount < 6) {
            this.scheduleRefresh(() => {
                this.currentIndex = 1;

                for (let i = 4; i <= 10; i++) {
                    this.refreshOwnedLine(i);
                }

                this.refreshCount += 1;
                this.sendCommand(true);
            }, 500, "all");
        } else {
            this.refreshMode = RefreshMode.NONE;
        }
    }

    // 재출력 C타입
    refreshCommandFuncC() {
        // 재출력 작업이 등록되어 있는경우
        if (this.refreshItem != null) {
            return
        }

        switch (this.refreshMode) {
            case RefreshMode.ALL_DISPLAY : this.refreshCommandFuncB(); break;
            case RefreshMode.GRAPHIC_DISPLAY : this.graphicRefresh(); break;
            case RefreshMode.TEXT_DISPLAY : this.textRefresh(); break;
            case RefreshMode.LIVE_DISPLAY : this.liveRefresh(); break;
            default:break;
        }
    }

    graphicRefresh() {
        if (this.refreshCount < 3) {
            this.currentIndex = 1

            for (let i = 1; i <= 3; i++) {
                this.refreshOwnedLine(i);
            }

            this.refreshCount += 1;
        } else if (this.refreshCount < 6) {
            this.scheduleRefresh(() => {
                this.currentIndex = 1;

                for (let i = 4; i <= 10; i++) {
                    this.refreshOwnedLine(i);
                }

                this.refreshCount += 1;
                this.sendCommand(true);
            }, 500, "graphic");
        } else {
            this.refreshMode = RefreshMode.NONE;
        }
    }

    textRefresh(){
        if (this.refreshCount < 3) {
            this.scheduleRefresh(() => {
                this.currentIndex = 0;
                this.refreshOwnedLine(0);

                this.refreshCount += 1;
                this.sendCommand(true);
            }, 1500, "text");
        } else {
            this.refreshMode = RefreshMode.NONE;
        }
    }

    liveRefresh(){
        if (this.refreshCount < 3) {
            this.scheduleRefresh(() => {
                this.currentIndex = 0;

                for (const i of this.refreshLine) {
                    this.refreshOwnedLine(i, RefreshMode.LIVE_DISPLAY);
                }

                this.refreshCount += 1;
                this.sendCommand(true);
            }, 1500, "live");
        } else {
            this.refreshLine = [];
            this.refreshMode = RefreshMode.NONE;
        }
    }

    setRefreshMode(mode) {
        // clearTimeout(this.refreshItem) 은 콜백 함수를 넘기던 것이라 타이머가 취소되지 않았다.
        this.clearRefreshTimer()

        // 요청이 곧 변경은 아니다. (예: GRAPHIC 상태에서 LIVE 요청은 무시된다)
        // 실제로 바뀐 경우에만 남긴다.
        const prevMode = this.refreshMode

        if (this.refreshMode !== RefreshMode.LIVE_DISPLAY && mode === RefreshMode.LIVE_DISPLAY) {
            this.refreshLine = []
        }
        
        if (this.refreshMode === RefreshMode.NONE) {
            this.refreshMode = mode
            this.refreshLine = []
        }
        else if (this.refreshMode !== RefreshMode.ALL_DISPLAY && this.refreshMode !== mode) {
            if (this.refreshMode === RefreshMode.GRAPHIC_DISPLAY) {
                if (mode === RefreshMode.TEXT_DISPLAY) {
                    this.refreshMode = RefreshMode.ALL_DISPLAY
                }
            }
            else if (this.refreshMode === RefreshMode.TEXT_DISPLAY) {
                if (mode !== RefreshMode.TEXT_DISPLAY) {
                    this.refreshMode = RefreshMode.ALL_DISPLAY
                }
            }
            else if (this.refreshMode === RefreshMode.LIVE_DISPLAY) {
                if (mode === RefreshMode.TEXT_DISPLAY) {
                    this.refreshMode = RefreshMode.ALL_DISPLAY
                }
                else if (mode === RefreshMode.GRAPHIC_DISPLAY) {
                    this.refreshMode = RefreshMode.GRAPHIC_DISPLAY
                }
            }
        }

        if (prevMode !== this.refreshMode) {
            dotPadTrace.log("mode", { from: prevMode, to: this.refreshMode, req: mode })
        }
    }

    compareString(str1, str2) {
        let start = -1;
        let end = -1;
    
        for (let i = 0; i < str1.length - 1; i += 2) {
            const char1 = str1.substring(i, i + 2);
            const char2 = str2.substring(i, i + 2);
    
            if (char1 !== char2) {
                if (start === -1) {
                    start = i;
                }
                end = i + 1;
            }
        }
    
        if (start !== -1) {
            return [start, end];
        } else {
            return null;
        }
    }
}

export {
    DotPadSendModule,
    RefreshMode
}
